interface NameSequence {
  start: number;
  step: number;
  count: number;
  padding: number;
  alphabetic: boolean;
}

type NameTemplateSegment =
  | { type: "literal"; value: string }
  | { type: "variable"; value: string; padding?: number }
  | { type: "sequence"; value: string; sequence: NameSequence };

export class NameTemplateError extends Error {}

function parseSequence(pattern: string): NameSequence | null {
  const numeric = pattern.match(/^(\d+)-(\d+)$/);
  if (numeric) {
    const startText = numeric[1];
    const endText = numeric[2];
    if (startText === undefined || endText === undefined) {
      return null;
    }
    const start = Number(startText);
    const end = Number(endText);
    const count = Math.abs(end - start) + 1;
    if (![start, end, count].every(Number.isSafeInteger)) {
      throw new NameTemplateError(
        "Numeric range endpoints and counts must be safe integers",
      );
    }
    const padded =
      (startText.length > 1 && startText.startsWith("0")) ||
      (endText.length > 1 && endText.startsWith("0"));
    return {
      start,
      step: start <= end ? 1 : -1,
      count,
      padding: padded ? Math.max(startText.length, endText.length) : 0,
      alphabetic: false,
    };
  }
  const alpha = pattern.match(/^([A-Za-z])-([A-Za-z])$/);
  if (!alpha) {
    return null;
  }
  const startText = alpha[1];
  const endText = alpha[2];
  if (startText === undefined || endText === undefined) {
    return null;
  }
  const start = startText.toUpperCase().charCodeAt(0);
  const end = endText.toUpperCase().charCodeAt(0);
  return {
    start,
    step: start <= end ? 1 : -1,
    count: Math.abs(end - start) + 1,
    padding: 0,
    alphabetic: true,
  };
}

export function parseNameTemplate(
  template: string,
  variables: readonly string[] = [],
): NameTemplateSegment[] {
  const segments: NameTemplateSegment[] = [];
  const patterns = /\{([^}]+)\}/g;
  let lastIndex = 0;
  let match;
  while ((match = patterns.exec(template)) !== null) {
    if (match.index > lastIndex) {
      segments.push({
        type: "literal",
        value: template.slice(lastIndex, match.index),
      });
    }
    const pattern = match[1];
    if (pattern === undefined) {
      throw new NameTemplateError("Invalid template pattern");
    }
    const variable = pattern.match(/^([A-Za-z_][A-Za-z_0-9]*)(?::(\d+))?$/);
    const variableName = variable?.[1];
    if (variableName !== undefined && variables.includes(variableName)) {
      const paddingText = variable?.[2];
      const padding =
        paddingText === undefined ? undefined : Number(paddingText);
      if (padding !== undefined && !Number.isSafeInteger(padding)) {
        throw new NameTemplateError("Variable padding must be a safe integer");
      }
      segments.push({ type: "variable", value: variableName, padding });
    } else {
      const sequence = parseSequence(pattern);
      segments.push(
        sequence
          ? { type: "sequence", value: pattern, sequence }
          : { type: "literal", value: match[0] },
      );
    }
    lastIndex = patterns.lastIndex;
  }
  if (lastIndex < template.length) {
    segments.push({ type: "literal", value: template.slice(lastIndex) });
  }
  return segments;
}

export function countNameTemplateResults(
  segments: readonly NameTemplateSegment[],
  maximum = Number.MAX_SAFE_INTEGER,
): number {
  if (!Number.isSafeInteger(maximum) || maximum < 1) {
    throw new NameTemplateError(
      "Maximum result count must be a positive safe integer",
    );
  }
  let count = 1;
  for (const segment of segments) {
    if (segment.type !== "sequence") {
      continue;
    }
    const combinations = count * segment.sequence.count;
    if (!Number.isSafeInteger(combinations) || combinations > maximum) {
      throw new NameTemplateError(
        `Template would generate more than ${maximum} labels`,
      );
    }
    count = combinations;
  }
  return count;
}

interface ExpandNameTemplateInput {
  template: string;
  variables?: Readonly<Record<string, string>>;
  maxResults: number;
  maxLength?: number;
  allowUnknownPatterns?: boolean;
}

function resolveVariable(
  segment: Extract<NameTemplateSegment, { type: "variable" }>,
  variables: Readonly<Record<string, string>>,
) {
  const value = variables[segment.value];
  if (value === undefined) {
    throw new NameTemplateError(`Unknown variable: ${segment.value}`);
  }
  const padding = /^\d+$/.test(value) ? (segment.padding ?? 0) : 0;
  return { value, padding, length: Math.max(value.length, padding) };
}

function validateExpansion(
  segments: readonly NameTemplateSegment[],
  input: ExpandNameTemplateInput,
) {
  countNameTemplateResults(segments, input.maxResults);
  let length = 0;
  for (const segment of segments) {
    if (segment.type === "literal") {
      if (!input.allowUnknownPatterns && /[{}]/.test(segment.value)) {
        throw new NameTemplateError(
          "Template contains an unknown or incomplete pattern",
        );
      }
      length += segment.value.length;
    } else if (segment.type === "variable") {
      length += resolveVariable(segment, input.variables ?? {}).length;
    } else {
      const sequence = segment.sequence;
      const end = sequence.start + (sequence.count - 1) * sequence.step;
      length += sequence.alphabetic
        ? 1
        : Math.max(
            sequence.padding,
            String(sequence.start).length,
            String(end).length,
          );
    }
  }
  if (input.maxLength !== undefined && length > input.maxLength) {
    throw new NameTemplateError(
      `Generated labels must be at most ${input.maxLength} characters`,
    );
  }
}

function renderSequence(sequence: NameSequence, index: number): string {
  const value = sequence.start + index * sequence.step;
  return sequence.alphabetic
    ? String.fromCharCode(value)
    : String(value).padStart(sequence.padding, "0");
}

export function expandNameTemplate(input: ExpandNameTemplateInput): string[] {
  const variables = input.variables ?? {};
  const segments = parseNameTemplate(input.template, Object.keys(variables));
  validateExpansion(segments, input);
  let names = [""];
  for (const segment of segments) {
    if (segment.type === "sequence") {
      names = names.flatMap((prefix) =>
        Array.from(
          { length: segment.sequence.count },
          (_, index) => prefix + renderSequence(segment.sequence, index),
        ),
      );
    } else {
      const variable =
        segment.type === "variable"
          ? resolveVariable(segment, variables)
          : null;
      const text =
        variable === null
          ? segment.value
          : variable.value.padStart(variable.padding, "0");
      names = names.map((prefix) => prefix + text);
    }
  }
  return names;
}
